import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AlertTriangle, Bot, CalendarClock, Coins, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';

type ExhaustionPolicy = 'stop' | 'continue_and_bill';

export interface AiUsagePeriodSummary {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  seatCount: number | null;
  unlimited: boolean;
  exhaustionPolicy: ExhaustionPolicy;
  allocatedAud: number | null;
  usedAud: number;
  remainingAud: number | null;
  overageAud: number;
  projectedOverageChargeAud: number;
  overageMarkupPercent: number;
  percentageUsed: number | null;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  webSearchCalls: number;
  exhaustedAt: string | null;
  overageBilledAud: number;
  overageBilledAt: string | null;
}

interface AiUsageData {
  organizationId: string;
  unlimited: boolean;
  exhaustionPolicy: ExhaustionPolicy;
  creditPerUserAud: number;
  overageMarkupPercent: number;
  current: AiUsagePeriodSummary;
  history: AiUsagePeriodSummary[];
}

interface AiUsagePanelProps {
  endpoint?: string;
  queryKey?: Array<string | number>;
  canEditPolicy?: boolean;
  compact?: boolean;
}

const currency = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const integer = new Intl.NumberFormat('en-AU');

function monthLabel(value: string) {
  return new Date(value).toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function dateLabel(value: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function AiUsagePanel({
  endpoint = '/ai-usage',
  queryKey = ['ai-usage'],
  canEditPolicy = false,
  compact = false,
}: AiUsagePanelProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<AiUsageData>(
    queryKey,
    () => api.get(`${endpoint}?months=12`).then((response) => response.data.data),
    { staleTime: 30_000 }
  );
  const policyMutation = useMutation(
    (exhaustionPolicy: ExhaustionPolicy) => api.patch('/ai-usage/policy', { exhaustionPolicy }),
    {
      onSuccess: () => queryClient.invalidateQueries(queryKey),
    }
  );

  if (isLoading) {
    return <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading AI usage…</div>;
  }
  if (error || !data) {
    return <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">AI usage data could not be loaded.</div>;
  }

  const current = data.current;
  const nearingLimit = current.percentageUsed !== null && current.percentageUsed >= 80;

  return (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      <div>
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-foreground">AI usage</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {data.unlimited
            ? 'Nexus usage is metered for visibility but has no monthly allocation limit.'
            : `${currency.format(data.creditPerUserAud)} per active user is pooled across the organisation each calendar month.`}
        </p>
      </div>

      <div className={`grid gap-3 ${data.unlimited ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4'}`}>
        {!data.unlimited && <Metric label="Allocated" value={currency.format(current.allocatedAud || 0)} sublabel={`${current.seatCount || 0} active users`} icon={<Coins className="h-4 w-4" />} />}
        <Metric label="Used" value={currency.format(current.usedAud)} sublabel={`${integer.format(current.totalTokens)} tokens`} icon={<Bot className="h-4 w-4" />} />
        {!data.unlimited && <Metric label="Remaining" value={currency.format(current.remainingAud || 0)} sublabel={current.exhaustedAt ? `Exhausted ${dateLabel(current.exhaustedAt)}` : monthLabel(current.periodStart)} icon={<CalendarClock className="h-4 w-4" />} />}
        {!data.unlimited && <Metric label="Current overage" value={currency.format(current.overageAud)} sublabel={current.overageAud > 0 ? `${currency.format(current.projectedOverageChargeAud)} incl. markup` : 'No overage'} icon={<AlertTriangle className="h-4 w-4" />} />}
      </div>

      {!data.unlimited && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{Math.min(100, current.percentageUsed || 0).toFixed(1)}% used</span>
            <span>Renews {dateLabel(new Date(new Date(current.periodEnd).getTime() + 1).toISOString())}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full rounded-full ${nearingLimit ? 'bg-amber-500' : 'bg-indigo-600'}`}
              style={{ width: `${Math.min(100, current.percentageUsed || 0)}%` }}
            />
          </div>
        </div>
      )}

      {canEditPolicy && !data.unlimited && (
        <fieldset className="space-y-3 rounded-lg border p-4">
          <legend className="px-1 text-sm font-semibold">When the allocation is exhausted</legend>
          <PolicyOption
            checked={data.exhaustionPolicy === 'stop'}
            disabled={policyMutation.isLoading}
            onChange={() => policyMutation.mutate('stop')}
            title="Stop AI usage"
            description="Block new AI requests until the allocation renews on the first day of the next month."
          />
          <PolicyOption
            checked={data.exhaustionPolicy === 'continue_and_bill'}
            disabled={policyMutation.isLoading}
            onChange={() => policyMutation.mutate('continue_and_bill')}
            title="Continue and bill overage"
            description={`Keep AI available and add provider cost above the allocation, plus a ${data.overageMarkupPercent}% markup, to the next billing cycle.`}
          />
          {policyMutation.isError && <p className="text-sm text-red-600">The policy could not be saved. Please try again.</p>}
        </fieldset>
      )}

      <div>
        <h4 className="mb-3 text-sm font-semibold text-foreground">Monthly history</h4>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[850px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Month</th>
                <th className="px-3 py-2 text-right">Allocated</th>
                <th className="px-3 py-2 text-right">Used</th>
                <th className="px-3 py-2 text-right">Remaining</th>
                <th className="px-3 py-2 text-right">Tokens</th>
                <th className="px-3 py-2">Allocation exhausted</th>
                <th className="px-3 py-2 text-right">Overage charge</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.history.map((period) => (
                <tr key={period.periodStart}>
                  <td className="px-3 py-2 font-medium">{monthLabel(period.periodStart)}</td>
                  <td className="px-3 py-2 text-right">{period.unlimited ? '—' : currency.format(period.allocatedAud || 0)}</td>
                  <td className="px-3 py-2 text-right">{currency.format(period.usedAud)}</td>
                  <td className="px-3 py-2 text-right">{period.unlimited ? '—' : currency.format(period.remainingAud || 0)}</td>
                  <td className="px-3 py-2 text-right">{integer.format(period.totalTokens)}</td>
                  <td className="px-3 py-2">{period.unlimited ? '—' : dateLabel(period.exhaustedAt)}</td>
                  <td className="px-3 py-2 text-right">{period.unlimited || period.projectedOverageChargeAud === 0 ? '—' : currency.format(period.projectedOverageChargeAud)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sublabel, icon }: { label: string; value: string; sublabel: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between text-sm text-muted-foreground"><span>{label}</span>{icon}</div>
      <div className="mt-2 text-2xl font-semibold text-card-foreground">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{sublabel}</div>
    </div>
  );
}

function PolicyOption(props: {
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
  title: string;
  description: string;
}) {
  return (
    <label className={`flex cursor-pointer gap-3 rounded-md border p-3 ${props.checked ? 'border-indigo-500 bg-indigo-50/60' : ''}`}>
      <input type="radio" checked={props.checked} disabled={props.disabled} onChange={props.onChange} className="mt-1" />
      <span>
        <span className="block font-medium text-foreground">{props.title}</span>
        <span className="mt-0.5 block text-sm text-muted-foreground">{props.description}</span>
      </span>
    </label>
  );
}
